import { useEffect, useRef, useState } from "react";

interface Props {
  value: string;
  placeholder?: string;
  /** Called after blur / debounce when content changed. */
  onSave: (text: string) => void | Promise<void>;
  readOnly?: boolean;
  /** Optional minimum height (CSS) — default 60px. */
  minHeight?: number;
  /** Whether to auto-focus on mount (useful for newly-created notes). */
  autoFocus?: boolean;
  testId?: string;
}

/**
 * B2.4 — Inline auto-sizing textarea for notebook notes (D7 default).
 *
 * Saves on blur. Debounced auto-save (700ms) so type-and-tab also works.
 * `tiptap` is intentionally out of scope here; markdown editing comes in B3+.
 */
export function InlineNoteEditor({
  value,
  placeholder = "Escribe tu apunte aquí…",
  onSave,
  readOnly,
  minHeight = 60,
  autoFocus,
  testId,
}: Props) {
  const [text, setText] = useState(value);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const lastSavedRef = useRef(value);

  // Resize to fit content
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-runs on text changes
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.max(minHeight, ta.scrollHeight)}px`;
  }, [text, minHeight]);

  // Sync external value (e.g. server returned a canonical version)
  useEffect(() => {
    if (value !== lastSavedRef.current) {
      setText(value);
      lastSavedRef.current = value;
    }
  }, [value]);

  // Debounced auto-save
  useEffect(() => {
    if (readOnly) return;
    if (text === lastSavedRef.current) return;
    const id = window.setTimeout(() => {
      lastSavedRef.current = text;
      void onSave(text);
    }, 700);
    return () => window.clearTimeout(id);
  }, [text, readOnly, onSave]);

  function handleBlur() {
    if (readOnly) return;
    if (text === lastSavedRef.current) return;
    lastSavedRef.current = text;
    void onSave(text);
  }

  return (
    <textarea
      ref={taRef}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={handleBlur}
      placeholder={placeholder}
      readOnly={readOnly}
      data-testid={testId ?? "inline-note-editor"}
      // biome-ignore lint/a11y/noAutofocus: explicit prop, only opt-in
      autoFocus={autoFocus}
      style={{ minHeight }}
      className="w-full resize-none bg-transparent text-sm leading-relaxed text-(--text-primary) outline-none focus:bg-(--bg-canvas) rounded px-1 -mx-1 transition-colors"
    />
  );
}
